import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://vedonica.in/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor — attach token & handle FormData ──────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (!config.data) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — refresh token on 401 ───────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        if (!refresh) throw new Error('No refresh token');
        const res = await axios.post(`${API_URL}/auth/token/refresh/`, { refresh });
        localStorage.setItem('access_token', res.data.access);
        original.headers.Authorization = `Bearer ${res.data.access}`;
        return api(original);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  register:        (data)     => api.post('/auth/register/', data),
  login:           (data)     => api.post('/auth/login/', data),
  logout:          (refresh)  => api.post('/auth/logout/', { refresh }),
  getProfile:      ()         => api.get('/auth/profile/'),
  updateProfile:   (data)     => api.patch('/auth/profile/', data),
  changePassword:  (data)     => api.post('/auth/change-password/', data),
  getAddresses:    ()         => api.get('/auth/addresses/'),
  addAddress:      (data)     => api.post('/auth/addresses/', data),
  updateAddress:   (id, data) => api.patch(`/auth/addresses/${id}/`, data),
  deleteAddress:   (id)       => api.delete(`/auth/addresses/${id}/`),
  requestOtp:      (data)     => api.post('/auth/request-otp/', data),
  loginWithOtp:    (data)     => api.post('/auth/verify-otp/', data),
  registerWithOtp: (data)     => api.post('/auth/register-otp/', data),
  verifyOtp:       (data)     => api.post('/auth/verify-otp/', data),
  getCustomers: () => api.get('/auth/admin/customers/'),
};

// ── Products ──────────────────────────────────────────────────────────────────
export const productAPI = {
  getAll:          ()          => api.get('/products/'),
  getOne:          (slug)      => api.get(`/products/${slug}/`),
  create:          (data)      => api.post('/products/create/', data),
  update:          (id, data)  => api.patch(`/products/${id}/manage/`, data),
  delete:          (id)        => api.delete(`/products/${id}/manage/`),
  uploadImages:    (id, formData) => api.post(`/products/${id}/images/`, formData),
  deleteImage:     (id, imageId)  => api.delete(`/products/${id}/images/${imageId}/`),
  getVariants:     (id)        => api.get(`/products/${id}/variants/`),
  createVariant:   (data)      => api.post(`/products/${data.product}/variants/`, data),
  updateVariant:   (id, data)  => api.patch(`/products/variants/${id}/`, data),
  getReviews:      (id)        => api.get(`/products/${id}/reviews/`),
  addReview:       (data)      => api.post(`/products/${data.product}/reviews/`, data),
  validateCoupon:  (data)      => api.post('/products/coupons/validate/', data),
  getAdminAll:     ()          => api.get('/products/admin/'),
  bulkUpdateOrder: (items) => api.post('/products/bulk-update-order/', items),
};

// ── Cart ──────────────────────────────────────────────────────────────────────
export const cartAPI = {
  get:        ()          => api.get('/cart/'),
  add:        (data)      => api.post('/cart/', data),
  updateItem: (id, data)  => api.patch(`/cart/items/${id}/`, data),
  removeItem: (id)        => api.delete(`/cart/items/${id}/`),
  clear:      ()          => api.delete('/cart/'),
};

// ── Orders ────────────────────────────────────────────────────────────────────
export const orderAPI = {
  // Customer
  list:   ()          => api.get('/orders/'),
  get:    (orderId)   => api.get(`/orders/${orderId}/`),
  place:  (data)      => api.post('/orders/place/', data),
  cancel: (orderId)   => api.post(`/orders/${orderId}/cancel/`),

  // Admin — order management
  adminList:         (params)        => api.get('/orders/admin/all/', { params }),
  adminGet:          (orderId)       => api.get(`/orders/admin/${orderId}/`),
  adminUpdateStatus: (orderId, data) => api.patch(`/orders/admin/${orderId}/status/`, data),
  adminStats:        ()              => api.get('/orders/admin/stats/'),

  // Admin — manual order placement
  track: (orderId) =>
    api.get(`/orders/track/${orderId}/`),
  adminPlaceOrder: (data) => api.post('/orders/admin/place-order/', data),

  // Razorpay
  // NOTE: amount must be a number (e.g. 999.00) — backend converts to paise
  createRazorpayOrder: (data) =>
  api.post('/orders/razorpay-order/', data),
  verifyRazorpayPayment: (data)   => api.post('/orders/razorpay-verify/', data),
};

export default api;